import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Send, Copy, Check, Users, User, Trash2 } from 'lucide-react';

interface ListMember {
  id: string;
  user_id: string;
  role: 'owner' | 'member';
  joined_at: string;
  email: string;
  full_name?: string;
}

interface ShareListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: (email: string) => Promise<{
    invitationLink?: string;
    listName?: string;
    userExists?: boolean;
  }>;
  listName: string;
  listId: string;
  currentUserId?: string;
}

const ShareListModal: React.FC<ShareListModalProps> = ({ isOpen, onClose, onShare, listName, listId, currentUserId }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [listNameFromResponse, setListNameFromResponse] = useState('');
  const [userExists, setUserExists] = useState(false);
  const [copied, setCopied] = useState(false);
  const [members, setMembers] = useState<ListMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ memberId: string; memberName: string } | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const { api } = await import('../utils/api');
      const response = await api.get(`/lists/${listId}/members`);
      if (response.ok) {
        const membersData = await response.json();
        setMembers(membersData);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [listId]);

  // Fetch list members when modal opens
  useEffect(() => {
    if (isOpen && listId) {
      fetchMembers();
    }
  }, [isOpen, listId, fetchMembers]);

  const removeMember = async (memberId: string) => {
    if (!currentUserId) return;

    setRemovingMember(memberId);
    try {
      const { api } = await import('../utils/api');
      const response = await api.delete(`/lists/${listId}/members/${memberId}`);

      if (response.ok) {
        // Remove the member from local state
        setMembers(members.filter(member => member.id !== memberId));
        setConfirmRemove(null);
      } else {
        const errorData = await response.json();
        alert(`Failed to remove member: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleRemoveClick = (member: ListMember) => {
    const memberName = member.full_name || member.email;
    setConfirmRemove({ memberId: member.id, memberName });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    try {
      const result = await onShare(email.trim());
      
      if (result.userExists) {
        // User already exists and was added to the list
        setListNameFromResponse(result.listName || listName);
        setUserExists(true);
        setInvitationLink(''); // No invitation link needed
        // Refresh members list to show the newly added user
        fetchMembers();
      } else if (result.invitationLink) {
        // New user - invitation link generated
        setInvitationLink(result.invitationLink);
        setListNameFromResponse(result.listName || '');
        setUserExists(false);
      }
      
      setEmail('');
    } catch (error) {
      console.error('Error sharing list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const resetModal = () => {
    setEmail('');
    setInvitationLink('');
    setListNameFromResponse('');
    setUserExists(false);
    setCopied(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Share List</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!invitationLink ? (
          <>
            {/* Current Members Section */}
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <Users className="w-5 h-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Current Members</h3>
              </div>
              
              {loadingMembers ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : members.length > 0 ? (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-500 mr-2" />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {member.full_name || member.email}
                          </span>
                          {member.full_name && (
                            <span className="text-xs text-gray-500 ml-2">
                              {member.email}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          member.role === 'owner' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {member.role}
                        </span>
                        {member.role !== 'owner' && (
                          <button
                            onClick={() => handleRemoveClick(member)}
                            disabled={removingMember === member.id}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove member"
                          >
                            {removingMember === member.id ? (
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No members yet</p>
              )}
            </div>

            <p className="text-gray-600 mb-6">
              Share "{listName}" with someone by entering their email address. You'll get a registration link to send them.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email address..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Generate Link
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="space-y-4">
            {userExists ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-green-800 font-medium">User Added Successfully!</p>
                    <p className="text-green-600 text-sm mt-1">
                      The user already has an account and has been added to "{listNameFromResponse}".
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 font-medium">Invitation Link Generated!</p>
                <p className="text-blue-600 text-sm mt-1">
                  Share this link with the person to invite them to "{listNameFromResponse}".
                </p>
              </div>
            )}

            {!userExists && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invitation Link
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-600 break-all">{invitationLink}</p>
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      title="Copy link"
                    >
                      {copied ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    <strong>How to share:</strong> Copy the link above and send it via email, messaging app, or any other method. 
                    When they click the link, they'll be able to create an account and automatically join the list.
                  </p>
                </div>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                onClick={resetModal}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Share Another
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmRemove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Remove Member</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to remove this member from the list?
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-900">
                  <strong>{confirmRemove.memberName}</strong> will no longer have access to this list.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setConfirmRemove(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => removeMember(confirmRemove.memberId)}
                  disabled={removingMember === confirmRemove.memberId}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {removingMember === confirmRemove.memberId ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Removing...
                    </div>
                  ) : (
                    'Remove Member'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareListModal;
